"use client";
import { env } from "@/env";
import { ColorSystemOptions, PaletteColorOptions, ThemeOptions, alpha, createTheme } from "@mui/material/styles";

const appEnv = env.NEXT_PUBLIC_ENV;
const envLocal = appEnv === "local";
const envAlpha = appEnv === "alpha";
const envBeta = appEnv === "beta";
const envStaging = appEnv === "staging";

interface SurfaceOptions {
  main: string;
  header: string;
  one: string;
  two: string;
  three: string;
  four: string;
  hover: string;
  selected: string;
  outline: string;
  contrastText: string;
  primary: string;
}

declare module "@mui/material/styles" {
  interface PaletteOptions {
    surface: SurfaceOptions;
    brand: {
      main: string;
      primary: string;
      contrastText: string;
    };
    white: PaletteColorOptions;
  }
  interface Palette {
    surface: SurfaceOptions;
  }
  interface TypeBackground {
    studio: string;
  }
  interface SimplePaletteColorOptions {
    subtle?: string;
  }
  interface TypographyVariants {
    label: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    label: true;
  }
}

declare module "@mui/material/AppBar" {
  interface AppBarPropsColorOverrides {
    brand: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsSizeOverrides {
    studio: true;
  }
}

export const lightModeColours = {
  primary: "#2D62AE",
  onPrimary: "#FFFFFF",
  success: "#50AE55",
  onSuccess: "#FFFFFF",
  error: "#DE0306",
  surface: {
    main: "#FFFFFF",
    header: "#F2F2F2",
    one: "#FFFFFF",
    two: "#FFFFFF",
    three: "#FFFFFF",
    four: "#FFFFFF",
    hover: "rgba(15, 113, 180, 0.08)",
    selected: "rgba(15, 113, 180, 0.05)",
    primary: alpha("#2D62AE", 0.12)
  },
  brand: "#2D3241",
  onBrand: "#FFFFFF",
  appBar: (() => {
    if (envLocal) return "#F44336";
    if (envAlpha) return "#7F2CCB";
    if (envBeta) return "#861657";
    if (envStaging) return "#FFA500";
    return "#2D3241";
  })(),
  outline: "rgba(0, 0, 0, 0.08)",
  onSurface: {
    highEmphasis: "#2D3241",
    mediumEmphasis: "#4A5A6A",
    disabled: "#6B7885"
  },
  background: "#F2F2F2",
  backgroundStudio: "#FFFFFF"
};

export const darkModeColours = {
  primary: "#51BEF0",
  onPrimary: "#031A25",
  success: "#7BC37F",
  onSuccess: "#1B2B1C",
  error: "#FE7F81",
  surface: {
    main: "#1C1B1F",
    header: alpha("#51BEF0", 0.05),
    one: "#1F2329", //alpha("#51BEF0", 0.05),
    two: "#202830", //alpha("#51BEF0", 0.08),
    three: "#222D36", //alpha("#51BEF0", 0.11),
    four: "#23323C", //alpha("#51BEF0", 0.14),
    hover: "rgba(81, 190, 240, 0.08)",
    selected: "rgba(81, 190, 240, 0.05)",
    primary: alpha("#51BEF0", 0.12)
  },
  brand: "#2D3241",
  appBar: (() => {
    if (envLocal) return "#D42316";
    if (envAlpha) return "#5F0CAB";
    if (envBeta) return "#861657";
    if (envStaging) return "#DF8500";
    return "#1F2329";
  })(),
  onBrand: "#FFFFFF",
  outline: "rgba(255, 255, 255, 0.14)",
  onSurface: {
    highEmphasis: "#EBF9FF",
    mediumEmphasis: "#BEC9CF",
    disabled: "#8C949C"
  },
  background: "#1C1B1F",
  backgroundStudio: "#1C1B1F"
};

type Colours = typeof darkModeColours;

// Seasonal colours — applied by the AppTheme provider when holiday mode is on and the date is in window
const halloweenColours = {
  primary: "#F77F00", // pumpkin
  onPrimary: "#1A0D00",
  surface: {
    hover: "rgba(247, 127, 0, 0.08)",
    selected: "rgba(247, 127, 0, 0.05)",
    primary: alpha("#F77F00", 0.12)
  }
};

const withHalloween = (colours: Colours): Colours => ({
  ...colours,
  ...halloweenColours,
  surface: { ...colours.surface, ...halloweenColours.surface }
});

const getPalette = (colours: Colours): ColorSystemOptions => ({
  palette: {
    primary: {
      main: colours.primary,
      contrastText: colours.onPrimary,
      subtle: alpha(colours.primary, 0.24)
    },
    success: {
      main: colours.success,
      contrastText: colours.onSuccess
    },
    error: { main: colours.error },
    AppBar: {
      darkBg: colours.appBar,
      defaultBg: colours.appBar
    },
    surface: {
      main: colours.surface.main,
      hover: colours.surface.hover,
      selected: colours.surface.selected,
      outline: colours.outline,
      contrastText: colours.primary,
      one: colours.surface.one,
      two: colours.surface.two,
      three: colours.surface.three,
      four: colours.surface.four,
      header: colours.surface.header,
      primary: colours.surface.primary
    },
    brand: {
      main: colours.brand,
      primary: colours.brand,
      contrastText: colours.onBrand
    },
    text: {
      primary: colours.onSurface.highEmphasis,
      secondary: colours.onSurface.mediumEmphasis,
      disabled: colours.onSurface.disabled
    },
    // foreground: {
    //   primary: colours.onSurface.highEmphasis,
    //   secondary: colours.onSurface.mediumEmphasis,
    //   disabled: colours.onSurface.disabled,
    // },
    background: {
      default: colours.background,
      paper: colours.surface.one,
      studio: colours.backgroundStudio
    },
    action: {
      hover: colours.surface.hover
    },
    white: {
      main: "#FFFFFF",
      contrastText: colours.primary
    }
  }
});

const sharedThemeOptions: ThemeOptions = {
  cssVariables: {
    colorSchemeSelector: "class"
  },

  typography: {
    fontFamily: '"Source Sans Pro"',
    // Headers Small
    h1: {
      fontWeight: 400,
      fontSize: "1.5rem",
      lineHeight: 1.3
    },
    // Titles Large
    h2: {
      fontWeight: 400,
      fontSize: "1.25rem",
      lineHeight: 1.2
    },
    // Titles Medium
    h3: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.5
    },
    // Titles Small
    h4: {
      fontWeight: 600,
      fontSize: "0.875rem",
      lineHeight: 1.4
    },
    // Subtitle2 Bold
    label: {
      fontWeight: 600,
      fontSize: "0.875rem",
      lineHeight: 1.57
    },
    body2: {
      lineHeight: 1.4
    },
    button: {
      fontWeight: 600,
      fontSize: "0.875rem",
      textTransform: "none"
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 600,
          letterSpacing: "1.25px",
          textTransform: "none"
        }
        // colorPlain: {
        //   backgroundColor: "var(--mui-palette-plain-main)",
        //   color: "var(--mui-palette-plain-contrastText)",
        //   "&:hover": {
        //     backgroundColor: "var(--mui-palette-plain-main)"
        //   }
        // }
      }
    },
    MuiCard: {
      defaultProps: {
        elevation: 1
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 1
      },
      styleOverrides: {
        elevation1: {
          backgroundImage: "none",
          backgroundColor: "var(--mui-palette-surface-one)"
        },
        elevation2: {
          backgroundImage: "none",
          backgroundColor: "var(--mui-palette-surface-two)"
        },
        elevation3: {
          backgroundImage: "none",
          backgroundColor: "var(--mui-palette-surface-three)"
        },
        elevation4: {
          backgroundImage: "none",
          backgroundColor: "var(--mui-palette-surface-four)"
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          label: "p"
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          lineHeight: 1.2,
          fontSize: "1rem"
        }
      }
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.MuiButton-sizeStudio": {
            fontSize: "0.875rem"
          }
        }
      }
    }
  }
};

const theme = createTheme({
  ...sharedThemeOptions,
  colorSchemes: {
    light: getPalette(lightModeColours),
    dark: getPalette(darkModeColours)
  }
});

// Seasonal variant — same base, palette swapped to halloween colours
const halloweenTheme = createTheme({
  ...sharedThemeOptions,
  colorSchemes: {
    light: getPalette(withHalloween(lightModeColours)),
    dark: getPalette(withHalloween(darkModeColours))
  }
});

export { theme, halloweenTheme };
